import React from 'react';
import { Casa } from '../../types';
import { Ocurrencia, colorDe, describirDia } from '../../utils/tareas';
import { Check, Circle, Clock, SkipForward } from 'lucide-react';

interface Props {
  o: Ocurrencia;
  casa?: Casa;
  /** Con varias casas a la vista, se dice de cuál es. */
  conCasa: boolean;
  /** Para las atrasadas: «Hace 2 días». */
  conDia?: boolean;
  compacta?: boolean;
  onMarcar: () => void;
  onAbrir: () => void;
}

/**
 * Una tarea en la semana. El círculo la marca como hecha de un toque; el
 * resto abre sus opciones (quién la hizo, saltar, posponer, editar).
 * Las futuras salen en gris: se ven, pero se marcan cuando les toque.
 */
export const TarjetaTarea: React.FC<Props> = ({ o, casa, conCasa, conDia, compacta, onMarcar, onAbrir }) => {
  const { tarea, estado, hecha } = o;
  const persona = hecha?.participanteId ?? tarea.asignadoA;
  const color = colorDe(casa, persona);
  const nombrePersona = casa?.participantes.find(p => p.id === persona)?.nombre;
  const hechaOSaltada = estado === 'hecha' || estado === 'saltada';
  const atrasada = conDia && estado === 'pendiente';

  const detalle = [
    conDia && describirDia(o.fecha),
    tarea.hora && !hechaOSaltada && tarea.hora,
    estado === 'saltada' ? 'Saltada' : null,
    !casa?.personal && (nombrePersona ? (hechaOSaltada ? `por ${nombrePersona}` : nombrePersona) : (!hechaOSaltada && 'Cualquiera')),
    conCasa && casa && casa.nombre,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`flex items-center gap-2 rounded-lg border-l-4 ${color.borde} border-y border-r
                     border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800
                     ${estado === 'futura' ? 'opacity-60' : ''} ${compacta ? 'px-1.5 py-1.5' : 'px-2 py-2'}`}>
      {estado === 'pendiente' ? (
        <button onClick={onMarcar} aria-label={`Marcar «${tarea.nombre}» como hecha`}
          className="shrink-0 p-1 rounded-full text-gray-400 hover:text-lumbre-600 dark:hover:text-lumbre-400 transition">
          <Circle className={compacta ? 'w-5 h-5' : 'w-6 h-6'} />
        </button>
      ) : estado === 'hecha' ? (
        <span className="shrink-0 p-1 text-emerald-600 dark:text-emerald-400" aria-label="Hecha">
          <span className={`flex items-center justify-center rounded-full bg-emerald-600 text-white ${compacta ? 'w-5 h-5' : 'w-6 h-6'}`}>
            <Check className="w-3.5 h-3.5" />
          </span>
        </span>
      ) : estado === 'saltada' ? (
        <span className="shrink-0 p-1 text-gray-400" aria-label="Saltada"><SkipForward className={compacta ? 'w-5 h-5' : 'w-6 h-6'} /></span>
      ) : (
        <span className="shrink-0 p-1 text-gray-300 dark:text-gray-600" aria-label="Más adelante">
          <Clock className={compacta ? 'w-5 h-5' : 'w-6 h-6'} />
        </span>
      )}

      <button onClick={onAbrir} className="min-w-0 flex-1 text-left flex items-center gap-2">
        {!compacta && <span className="text-xl shrink-0" aria-hidden>{tarea.icono}</span>}
        <span className="min-w-0">
          <span className={`block font-semibold ${compacta ? 'text-xs line-clamp-2 break-words' : 'text-sm truncate'} ${
            hechaOSaltada ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
            {compacta && <span aria-hidden>{tarea.icono} </span>}{tarea.nombre}
          </span>
          {detalle && (
            <span className={`block truncate ${compacta ? 'text-[10px]' : 'text-[11px]'} ${
              atrasada ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {nombrePersona && !casa?.personal && <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${color.punto}`} />}
              {detalle}
            </span>
          )}
        </span>
      </button>
    </div>
  );
};
